import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import OrganizationApiEndpoints from './OrganizationApiEndpoints';
import CreateApiKeyDialog from './CreateApiKeyDialog';
import { DeleteIcon, KeyIcon, AddIcon, ApiKeysIcon } from '@common/utils/icons/icons';
import GeneralLoaderWrapper from '@common/components/General/GeneralLoaderWrapper';
import CustomSplitColumnCard from '@common/components/misc/CustomSplitColumnCard';
import TooltipIconButton from '@common/components/Inputs/TooltipIconButton';
import CustomSnackbar from '@common/components/Feedback/CustomSnackbar';
import CustomConfirmAbortDialog from '../../misc/CustomConfirmAbortDialog';
import CustomAlert from '@common/components/Feedback/CustomAlert';
import SwitchInput from '../../forms/SwitchInput';
import { dateToStringFormat } from '@common/utils/date';
import { useAppDispatch, useAppSelector } from '../../../store';
import {
  selectExternalApi,
  selectCurrentOrganizationId,
  selectOrganizationsData,
} from '../../../store/organization/organization-slice-selectors';
import {
  selectIsUserAdminOrHigher,
  selectUser,
} from '../../../store/general/general-slice-selectors';
import {
  getApiKeysByOrganizationId,
  createApiKey,
  deleteApiKeyById,
  patchOrganizationById,
} from '../../../store/organization/organization-actions';
import {
  resetExternalApiRestState,
  resetOrganizationsRestState,
  clearCreatedApiKey,
} from '../../../store/organization/organization-slice';
import HttpFetchStatus from '@common/enums/api/http-fetch-status';
import { isHttpError } from '@common/interfaces/api/http-error';
import { isValidationResultArray } from '@common/interfaces/api/validation-result';
import OrganizationRole from '../../../models/enums/organization-roles';
import { PostApiKey } from '../../../models/organization/external-api';

const OrganizationApiKeySettings: React.FC = () => {
  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const user = useAppSelector((state) => selectUser(state));
  const isAdmin = useAppSelector((state) => selectIsUserAdminOrHigher(state));
  const externalApi = useAppSelector((state) => selectExternalApi(state));
  const currentOrganizationId = useAppSelector((state) => selectCurrentOrganizationId(state));
  const organizationData = useAppSelector((state) => selectOrganizationsData(state));

  const [deleteApiKeyId, setDeleteApiKeyId] = useState<string | null>(null);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);

  useEffect(() => {
    if (currentOrganizationId && externalApi.rest.getStatus === HttpFetchStatus.Idle) {
      dispatch(getApiKeysByOrganizationId(currentOrganizationId));
    }
  }, [currentOrganizationId, dispatch, externalApi.rest.getStatus]);

  useEffect(() => {
    return () => {
      // remove the revealed secret from store on page unload
      dispatch(clearCreatedApiKey());
    };
  }, [dispatch]);

  const isViewedOrganizationManufacturer =
    organizationData.current?.role === OrganizationRole.MANUFACTURER;
  const createApiKeyError = externalApi.rest.postError;
  const createApiKeyErrorMessage =
    createApiKeyError == null
      ? null
      : isHttpError(createApiKeyError)
        ? createApiKeyError.message
        : isValidationResultArray(createApiKeyError)
          ? createApiKeyError
              .map((validationResult) =>
                validationResult.error != null ? t(validationResult.error) : '',
              )
              .filter((message) => message.length > 0)
              .join(' ') || null
          : null;

  const toggleApiKeyEnabledHandler = () => {
    if (currentOrganizationId != null) {
      dispatch(
        patchOrganizationById({
          id: currentOrganizationId,
          data: { isExternalApiEnabled: !organizationData.current?.isExternalApiEnabled },
        }),
      );
    }
  };

  const openCreateDialogHandler = () => {
    setIsCreateDialogOpen(true);
  };

  const closeCreateDialogHandler = () => {
    setIsCreateDialogOpen(false);
    dispatch(
      resetExternalApiRestState({
        postStatus: HttpFetchStatus.Idle,
        postError: null,
      }),
    );
    dispatch(clearCreatedApiKey());
  };

  const createApiKeyHandler = (payload: PostApiKey) => {
    dispatch(createApiKey(payload));
  };

  const openConfirmDeleteDialogClickHandler = (id: string) => {
    setDeleteApiKeyId(id);
  };

  const closeConfirmDeleteDialogHandler = () => {
    setDeleteApiKeyId(null);
  };

  const deleteApiKeyHandler = () => {
    if (deleteApiKeyId != null) {
      dispatch(deleteApiKeyById(deleteApiKeyId));
      setDeleteApiKeyId(null);
    }
  };

  const closeDeleteApiKeySnackbarHandler = () => {
    dispatch(
      resetExternalApiRestState({
        deleteStatus: HttpFetchStatus.Idle,
        deleteError: null,
      }),
    );
  };

  const closeSnackbarHandlerOrganization = () => {
    dispatch(
      resetOrganizationsRestState({
        patchStatus: HttpFetchStatus.Idle,
        patchError: null,
      }),
    );
  };

  return (
    <React.Fragment>
      <GeneralLoaderWrapper
        getStatus={externalApi.rest.getStatus}
        loadingMessage={t('loading-api-key-settings')}
        dataLoaded={externalApi.data != null}
        failedToLoadMessage={
          externalApi.rest.getError?.message ?? t('error-loading-api-key-settings')
        }
      >
        <Stack spacing={2} width="100%" alignItems="center" p={0.3}>
          <CustomSplitColumnCard
            title={t('title-api-key-settings')}
            content={[
              isAdmin && user.data.organization.role === OrganizationRole.MANUFACTURER
                ? {
                    left: (
                      <Stack direction="row" alignItems="center" spacing={1} py={1}>
                        <KeyIcon />
                        <Typography>{t('input-label-api-key-enabled')}</Typography>
                      </Stack>
                    ),
                    right: (
                      <SwitchInput
                        label={
                          <Stack>
                            {t('button-enable-feature')}
                            <Typography variant="caption" component="div">
                              {t('form-help-text-external-api-enabled')}
                            </Typography>
                          </Stack>
                        }
                        isChecked={organizationData.current?.isExternalApiEnabled ?? false}
                        onChange={toggleApiKeyEnabledHandler}
                      />
                    ),
                  }
                : null,
              {
                left: (
                  <Stack spacing={3} pr={2} mb={2}>
                    <Stack direction="row" alignItems="center" spacing={1} py={1}>
                      <ApiKeysIcon />
                      <Typography>{t('title-api-keys-list')}</Typography>
                    </Stack>
                  </Stack>
                ),
                right: (
                  <Stack spacing={3} mb={2} width="100%">
                    {isAdmin && (
                      <Button
                        variant="outlined"
                        onClick={openCreateDialogHandler}
                        fullWidth
                        size="large"
                        startIcon={<AddIcon />}
                      >
                        {t('button-create-api-key')}
                      </Button>
                    )}

                    {externalApi.data?.map((data, i) => (
                      <Stack direction="row" justifyContent="space-between" key={i}>
                        <Stack>
                          <Typography>{data.name}</Typography>
                          <Typography variant="caption" component="div">
                            {data.prefix}…
                          </Typography>
                          <Typography variant="caption" component="div">
                            {t('label-api-key-scopes')}:{' '}
                            {data.scopes.length > 0
                              ? data.scopes.join(', ')
                              : t('label-api-key-scopes-none')}
                          </Typography>
                          <Typography variant="caption" id="label-expiry-date">
                            {t('label-expiry-date')}: {dateToStringFormat(data.dateExpires)}
                          </Typography>
                        </Stack>
                        <Stack direction="row">
                          {isAdmin && (
                            <TooltipIconButton
                              icon={<DeleteIcon color="error" data-testid="delete-code-button" />}
                              onClick={() => openConfirmDeleteDialogClickHandler(data.id)}
                              tooltip={t('info-tooltip-delete')}
                              tooltipProps={{ placement: 'right' }}
                            />
                          )}
                        </Stack>
                      </Stack>
                    ))}
                  </Stack>
                ),
              },
            ]}
          />
          <OrganizationApiEndpoints />
        </Stack>
      </GeneralLoaderWrapper>
      <CreateApiKeyDialog
        open={isCreateDialogOpen}
        organizationId={currentOrganizationId ?? ''}
        isManufacturer={isViewedOrganizationManufacturer}
        createdKey={
          externalApi.rest.postStatus === HttpFetchStatus.Success
            ? (externalApi.created ?? null)
            : null
        }
        errorMessage={createApiKeyErrorMessage}
        isSubmitting={externalApi.rest.postStatus === HttpFetchStatus.Loading}
        onCreate={createApiKeyHandler}
        onClose={closeCreateDialogHandler}
      />
      <CustomConfirmAbortDialog
        open={deleteApiKeyId != null}
        title={t('title-delete-api-key')}
        text={<CustomAlert severity="warning" header={t('warning-deleting-is-permanent')} />}
        confirmButton={t('button-delete')}
        abortButton={t('button-cancel')}
        onAbort={closeConfirmDeleteDialogHandler}
        onConfirm={deleteApiKeyHandler}
      />
      <CustomSnackbar
        open={externalApi.rest.deleteStatus === HttpFetchStatus.Success}
        title={t('success-deleting-api-key')}
        onClose={closeDeleteApiKeySnackbarHandler}
      />
      <CustomSnackbar
        open={organizationData.rest.patchStatus === HttpFetchStatus.Success}
        title={t('success-patching-toggle-api-key')}
        onClose={closeSnackbarHandlerOrganization}
      />
      <CustomSnackbar
        open={externalApi.rest.deleteStatus === HttpFetchStatus.Failed}
        severity="error"
        title={t('error-deleting-api-key')}
        onClose={closeDeleteApiKeySnackbarHandler}
        textMessages={
          externalApi.rest.deleteError != null && isHttpError(externalApi.rest.deleteError)
            ? [externalApi.rest.deleteError.message]
            : undefined
        }
      />
      <CustomSnackbar
        open={organizationData.rest.patchStatus === HttpFetchStatus.Failed}
        severity="error"
        title={t('error-patching-toggle-api-key')}
        onClose={closeSnackbarHandlerOrganization}
        textMessages={
          organizationData.rest.patchError != null && isHttpError(organizationData.rest.patchError)
            ? [organizationData.rest.patchError.message]
            : undefined
        }
      />
    </React.Fragment>
  );
};
export default OrganizationApiKeySettings;
